import { addKill, addScore } from './state.js';
import { mulberry32 } from '../util/math.js';

const metaKey = 'dead-loop-meta-v1';
const dailyModifiers = ['noManualAim', 'doubleEnemies', 'halfHealth', 'oneBullet'];
const powerUpgradeIds = new Set([
  'ricochet',
  'orbitals',
  'skyLaser',
  'freezeAura',
  'chainLightning',
  'stormCall',
  'deathNova',
  'vampire',
  'barrier',
  'explode',
  'freeze',
  'stagger',
  'execute',
  'blackHole',
  'bladeAura',
  'meteorRain',
  'shockWeb',
]);

export const upgradePool = [
  { id: 'moveSpeed', name: 'Quickstep Serum', rarity: 'common', stat: 'moveSpeed', amount: 0.12, description: '+12% move speed.' },
  { id: 'damage', name: 'Hollowpoint Loop', rarity: 'rare', stat: 'damage', amount: 0.16, description: '+16% weapon damage.' },
  { id: 'fireRate', name: 'Pulse Relay', rarity: 'rare', stat: 'fireRate', amount: 0.12, description: '+12% fire rate.' },
  { id: 'bulletSize', name: 'Ion Jacket', rarity: 'epic', stat: 'bulletSize', amount: 0.14, description: '+14% projectile size.' },
  { id: 'pellets', name: 'Scatter Core', rarity: 'epic', stat: 'pellets', amount: 1, description: '+1 pellet on compatible weapons.' },
  { id: 'pierce', name: 'Obsidian Needle', rarity: 'legendary', stat: 'pierce', amount: 1, description: '+1 projectile pierce.' },
  { id: 'ricochet', name: 'Ricochet Core', rarity: 'legendary', stat: 'ricochet', amount: 2, description: 'Bullets bounce twice off obstacles.' },
  { id: 'orbitals', name: 'Orbit Knife', rarity: 'epic', stat: 'orbitals', amount: 1, description: '+1 high-damage orbital blade around the hero.' },
  { id: 'skyLaser', name: 'Judgement Beam', rarity: 'legendary', stat: 'skyLaser', amount: 1, description: 'A sky laser periodically burns a cluster.' },
  { id: 'freezeAura', name: 'Cryo Halo', rarity: 'epic', stat: 'freezeAura', amount: 1, description: 'Nearby enemies are slowed.' },
  { id: 'chainLightning', name: 'Chain Spark', rarity: 'epic', stat: 'chainLightning', amount: 2, description: 'Hits jump lightning to nearby enemies.' },
  { id: 'stormCall', name: 'Storm Call', rarity: 'rare', stat: 'stormCall', amount: 1, description: 'Lightning strikes a random enemy.' },
  { id: 'deathNova', name: 'Death Nova', rarity: 'epic', stat: 'deathNova', amount: 1, description: 'Killed enemies explode into a shockwave.' },
  { id: 'vampire', name: 'Blood Battery', rarity: 'rare', stat: 'vampire', amount: 1, description: 'Kills restore health.' },
  { id: 'regen', name: 'Nanite Regen', rarity: 'rare', stat: 'regen', amount: 1, description: 'Slowly regenerate health.' },
  { id: 'barrier', name: 'Kinetic Barrier', rarity: 'epic', stat: 'barrier', amount: 1, description: 'Contact damage is reduced.' },
  { id: 'explode', name: 'Blast Rounds', rarity: 'legendary', stat: 'explode', amount: 1, description: 'Shots explode on impact or timeout.' },
  { id: 'freeze', name: 'Frost Rounds', rarity: 'rare', stat: 'freeze', amount: 1, description: 'Shots freeze enemies on hit.' },
  { id: 'critChance', name: 'Execution Lens', rarity: 'rare', stat: 'critChance', amount: 0.14, description: '+14% chance for heavy crit shots.' },
  { id: 'critDamage', name: 'Glassfang Optics', rarity: 'epic', stat: 'critDamage', amount: 0.35, description: '+35% critical hit damage.' },
  { id: 'bulletSpeed', name: 'Rail Accelerator', rarity: 'common', stat: 'bulletSpeed', amount: 0.2, description: '+20% projectile speed.' },
  { id: 'stagger', name: 'Hammer Pulse', rarity: 'rare', stat: 'stagger', amount: 1, description: 'Hits shove and briefly slow targets.' },
  { id: 'execute', name: 'Redline Reaper', rarity: 'legendary', stat: 'execute', amount: 1, description: 'Low-health enemies shatter on your next hit.' },
  { id: 'overdriveGain', name: 'Adrenal Dynamo', rarity: 'rare', stat: 'overdriveGain', amount: 0.25, description: '+25% overdrive charge from kills.' },
  { id: 'mercy', name: 'Entry Aegis', rarity: 'common', stat: 'mercy', amount: 1, description: 'Longer safe window at the start of waves.' },
  { id: 'blackHole', name: 'Singularity Mine', rarity: 'legendary', stat: 'blackHole', amount: 1, description: 'Periodically crushes and pulls the biggest horde cluster.' },
  { id: 'bladeAura', name: 'Shredder Halo', rarity: 'epic', stat: 'bladeAura', amount: 1, description: 'A close-range blade field carves enemies around you.' },
  { id: 'meteorRain', name: 'Orbital Rain', rarity: 'legendary', stat: 'meteorRain', amount: 1, description: 'Multiple orbital impacts hunt priority targets.' },
  { id: 'shockWeb', name: 'Voltage Web', rarity: 'epic', stat: 'shockWeb', amount: 1, description: 'Packed enemies arc lightning into each other.' },
];

export function grantKillRewards(game, enemy) {
  addKill();
  addScore(enemy?.scoreValue || 0);
  addXp(game, enemy?.xpValue || 0);
  awardDarkCoins(Math.floor((enemy?.scoreValue || 0) / 100) + (enemy?.boss ? 5 : 0));
}

export function addXp(game, amount) {
  game.xp += Math.max(0, amount || 0);
  while (game.xp >= game.xpToLevel) {
    game.xp -= game.xpToLevel;
    game.level += 1;
    game.xpToLevel = Math.floor(game.xpToLevel * 1.14 + 8);
    game.pendingUpgrade = true;
  }
  return game.xp;
}

export function createUpgradeChoices(count = 4) {
  const selected = [];
  const powers = shuffle(upgradePool.filter((upgrade) => powerUpgradeIds.has(upgrade.id)));
  const basics = shuffle(upgradePool.filter((upgrade) => !powerUpgradeIds.has(upgrade.id)));
  while (selected.length < Math.min(2, count) && powers.length) selected.push(powers.pop());
  for (const upgrade of shuffle([...powers, ...basics])) {
    if (selected.length >= count) break;
    if (!selected.some((choice) => choice.id === upgrade.id)) selected.push(upgrade);
  }
  return selected;
}

export function applyUpgrade(game, upgrade) {
  if (!upgrade?.stat) return game.upgrades;
  game.upgrades[upgrade.stat] = (game.upgrades[upgrade.stat] || 0) + upgrade.amount;
  game.pendingUpgrade = false;
  return game.upgrades;
}

function shuffle(items) {
  return [...items].sort(() => Math.random() - 0.5);
}

export function getDailyChallenge(date = new Date()) {
  const day = date.toISOString().slice(0, 10);
  const seed = Math.floor(Date.parse(`${day}T00:00:00.000Z`) / 86400000);
  const rng = mulberry32(seed);
  return {
    day,
    seed,
    modifier: dailyModifiers[Math.floor(rng() * dailyModifiers.length)],
    leaderboardKey: `looper-daily-${day}`,
  };
}

export function applyDailyChallenge(game, challenge = getDailyChallenge()) {
  game.daily = challenge;
  if (challenge.modifier === 'halfHealth') {
    game.maxHealth = Math.max(1, Math.floor(game.maxHealth * 0.5));
    game.health = Math.min(game.health, game.maxHealth);
  }
  if (challenge.modifier === 'doubleEnemies') game.spawnMultiplier = 2;
  if (challenge.modifier === 'oneBullet') game.upgrades.pellets = Math.min(game.upgrades.pellets || 0, 0);
  return game;
}

export function loadMetaProgression() {
  try {
    return {
      darkCoins: 0,
      unlocked: [],
      ...(JSON.parse(localStorage.getItem(metaKey) || '{}')),
    };
  } catch {
    return { darkCoins: 0, unlocked: [] };
  }
}

export function saveMetaProgression(meta) {
  localStorage.setItem(metaKey, JSON.stringify(meta));
  return meta;
}

export function awardDarkCoins(amount = 0) {
  const meta = loadMetaProgression();
  meta.darkCoins += Math.max(0, Math.floor(amount));
  saveMetaProgression(meta);
  return meta.darkCoins;
}

export function buyMetaUnlock(id, cost) {
  const meta = loadMetaProgression();
  if (meta.unlocked.includes(id) || meta.darkCoins < cost) return false;
  meta.darkCoins -= cost;
  meta.unlocked.push(id);
  saveMetaProgression(meta);
  return true;
}

export const metaUnlockPool = [
  { id: 'variant-neon-smg', label: 'Neon SMG skin', cost: 25, type: 'weaponVariant' },
  { id: 'start-quickstep', label: 'Start with Quickstep', cost: 45, type: 'startingUpgrade' },
  { id: 'hero-obsidian', label: 'Obsidian hero tint', cost: 35, type: 'heroTint' },
  { id: 'music-ritual', label: 'Ritual music track', cost: 55, type: 'musicTrack' },
];
